import { useState } from 'react';
import { Image } from 'antd';
import useIsDesktop from '../hooks/useIsDesktop';
import { initials, renLabel } from '../lib/format';
import { AVATAR_IMG } from '../lib/avatar';
import PhotoViewer from './PhotoViewer';

/**
 * The listing agent's profile card — the face, name, REN and number of whoever
 * holds a listing, with the two ways an agent actually reaches a colleague.
 *
 * This is the ONLY contact on a co-broke listing: the property owner's details
 * are redacted server-side, so a co-broke starts here and not with the vendor.
 * The number is printed as text as well as wired to the buttons — an agent
 * often wants to save the contact, forward it or read it out, and a pair of
 * tap-to-act buttons gives them none of that.
 *
 * HQ-listed stock has no person behind it (`is_hq`, no phone): the card still
 * renders, minus the actions, so the page never claims a contact that isn't
 * there.
 *
 * `label` overrides the eyebrow for the co-share screens, where the person on the
 * card is just as often the agent ASKING to co-share as the one holding the
 * listing — printing "LISTING AGENT" over the requester's face names the wrong
 * role in the one place where which role it is decides who does what next.
 */
export default function AgentCard({ agent, note, waText, label }) {
  // Above the `!agent` guard on purpose — a card that mounts without an agent and
  // gets one on a later render must not change how many hooks it calls.
  const [viewingPhoto, setViewingPhoto] = useState(false);
  const desktop = useIsDesktop();

  if (!agent) return null;
  const { name, phone, email, photo_url: photo, ren_no: ren, is_hq: isHq } = agent;
  const ren_ = String(ren || '').replace(/^REN\s*/i, '');

  return (
    // One surface, one padding box: identity, number and actions are all the same
    // card. The buttons used to sit in a tinted footer strip, which read as a bar
    // stuck to the bottom rather than part of the card.
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 11 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <Avatar photo={photo} name={name} isHq={isHq} onOpen={photo ? () => setViewingPhoto(true) : null} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.08em', color: 'var(--muted)' }}>
            {label || (isHq ? 'CID HQ STOCK' : 'LISTING AGENT')}
          </div>
          <div style={{ fontSize: 14.5, fontWeight: 700, letterSpacing: '-.012em', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {name}
          </div>
          {ren_ && (
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 1 }}>{renLabel(ren_)}</div>
          )}
        </div>
      </div>

      {(phone || email) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {phone && (
            <div style={{ fontSize: 13.5, fontWeight: 600, fontFamily: 'ui-monospace,monospace', letterSpacing: '-.01em' }}>
              {phone}
            </div>
          )}
          {email && (
            <div style={{ fontSize: 11.5, color: 'var(--muted)', wordBreak: 'break-all' }}>{email}</div>
          )}
        </div>
      )}

      {note && (
        <div style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--muted)' }}>{note}</div>
      )}

      {(phone || email) && (
        <div style={{ display: 'flex', gap: 10 }}>
          {phone && (
            <a href={telHref(phone)} style={{ ...actionBtn, flex: 1, background: 'var(--primary)', color: '#fff' }}>
              <PhoneIcon />
              Call
            </a>
          )}
          {phone && (
            <a
              href={`https://wa.me/${waNumber(phone)}${waText ? `?text=${encodeURIComponent(waText)}` : ''}`}
              target="_blank"
              rel="noreferrer"
              style={{ ...actionBtn, flex: 1, background: WA_GREEN, color: '#fff' }}
            >
              <WhatsAppIcon />
              WhatsApp
            </a>
          )}
          {/* Email is the fallback, not a peer of the other two — an icon-width
              button so it never competes with call/WhatsApp for the thumb. Tinted
              rather than white: on the card's own surface a bordered white button
              would read as an empty box. */}
          {email && (
            <a
              href={`mailto:${email}`}
              aria-label={`Email ${name}`}
              style={{ ...actionBtn, width: 46, flex: 'none', background: 'var(--chip)', border: '1px solid var(--border)', color: 'var(--text)' }}
            >
              <MailIcon />
            </a>
          )}
        </div>
      )}

      {/* Desktop opens the photo as a lightbox over the page; the phone keeps the
          full-screen rising sheet, which is the shape every other overlay takes
          there. Both show the photo WHOLE — the avatar frame is a top-anchored
          crop (lib/avatar.js), so on a full-length portrait the face you tapped
          is a thumbnail inside it.

          antd's preview is safe for these even though PhotoViewer had to avoid
          FileViewer: it renders a plain <img src>, and it is the fetch — not the
          image — that the legacy erp213 Azure container blocks with no CORS
          headers. Nothing here reads the bytes. */}
      {desktop ? (
        <Image
          src={photo}
          alt=""
          style={{ display: 'none' }}
          preview={{
            visible: viewingPhoto,
            src: photo,
            onVisibleChange: (v) => setViewingPhoto(v),
          }}
        />
      ) : (
        <PhotoViewer
          open={viewingPhoto}
          url={photo}
          title={name}
          onClose={() => setViewingPhoto(false)}
        />
      )}
    </div>
  );
}

// Photo when there is one, initials when there isn't. HQ has no face at all, so
// it gets the building mark rather than the letters "CH".
//
// The photo is framed by AVATAR_IMG, which crops to the top of the image rather
// than its centre — see lib/avatar.js for why a centred crop showed the jacket.
//
// `onOpen` is passed only when there is a photo to open: the initials and the HQ
// building mark are all there is to see, so making them tappable would promise a
// bigger version of nothing.
function Avatar({ photo, name, isHq, onOpen }) {
  const box = {
    width: 50,
    height: 50,
    borderRadius: '50%',
    flex: 'none',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--chip)',
    border: '1px solid var(--border)',
    color: 'var(--primary)',
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: '.02em',
  };
  if (photo) {
    return (
      <div
        className={onOpen ? 'tap' : undefined}
        onClick={onOpen || undefined}
        role={onOpen ? 'button' : undefined}
        tabIndex={onOpen ? 0 : undefined}
        aria-label={onOpen ? `View ${name || 'agent'}'s photo` : undefined}
        onKeyDown={onOpen ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } } : undefined}
        style={{ ...box, cursor: onOpen ? 'pointer' : undefined }}
      >
        <img src={photo} alt="" style={AVATAR_IMG} />
      </div>
    );
  }
  return (
    <div style={box}>
      {isHq ? (
        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 21V6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v15" />
          <path d="M15 10h3a2 2 0 0 1 2 2v9" />
          <path d="M2 21h20M8 8h3M8 12h3M8 16h3" />
        </svg>
      ) : (
        initials(name) || '—'
      )}
    </div>
  );
}

// Malaysian mobiles are stored as 012-345 6789 / +6012…; wa.me wants digits with
// the country code and nothing else.
const waNumber = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.startsWith('60')) return digits;
  return `60${digits.replace(/^0+/, '')}`;
};

// tel: is a URI, and the stored format carries a space ("012-345 6789"). A raw
// space is invalid there and some dialers truncate the number at it, so hand over
// digits and an optional leading + only.
const telHref = (phone) => `tel:${String(phone || '').replace(/[^\d+]/g, '')}`;

const WA_GREEN = '#128C7E';

const actionBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 7,
  height: 42,
  borderRadius: 11,
  fontSize: 13,
  fontWeight: 600,
  textDecoration: 'none',
  border: '1px solid transparent',
};

const PhoneIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
    <path d="m3.5 7 8.5 5.5L20.5 7" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.16-.17.2-.35.22-.64.08-.3-.15-1.26-.47-2.4-1.48-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.45-.53.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.48-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.87 1.21 3.07.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.62.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2-1.41.25-.7.25-1.29.18-1.42-.08-.12-.28-.2-.57-.34M12.05 21.8h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.89 9.89-9.89 2.64 0 5.12 1.03 6.99 2.9a9.83 9.83 0 0 1 2.89 6.99c0 5.45-4.44 9.89-9.88 9.89M20.46 3.5A11.82 11.82 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.9c0 2.09.54 4.14 1.58 5.94L.06 24l6.3-1.65a11.88 11.88 0 0 0 5.69 1.45c6.55 0 11.89-5.34 11.89-11.9 0-3.18-1.24-6.17-3.48-8.41" />
  </svg>
);
